import type { Styles } from '@adornis/ass/style.js';
import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-tailwind-lit-element.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Status, UserPresenceRecord } from '@chancenwerk/shared/PresenceRecord.js';
import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

export type IndexedValueEvent<T> = ValueEvent<T> & { detail: { index: number } };

/**
 * @customElement bboard-component-attendance-card
 */
@customElement('board-component-attendance-card-list')
export class BoardComponentAttendanceCardList extends ChemistryLitElement {
  @property({ attribute: false }) records: UserPresenceRecord[] = [];
  @property({ attribute: 'empty-name' }) emptyName: string = '';

  override styles(): Styles[] {
    return [
      ...super.styles(),
      {
        ':host': {
          'board-component-attendance-card': { width: `calc(33% - ${this.spacing.sm} / 3)` },
          '@media screen and (max-width: 1400px)': {
            'board-component-attendance-card': { width: `calc(50% - ${this.spacing.sm} / 2)` },
          },
          '@media screen and (max-width: 900px)': { 'board-component-attendance-card': { width: '100%' } },
        },
      },
    ];
  }

  private _emitStatusClick(i: number, status: Status) {
    const eventDis = new CustomEvent('status-click', { detail: { value: status, index: i } });
    this.dispatchEvent(eventDis);
  }

  private _emitCommentChanged(i: number, text: string) {
    const eventDis = new CustomEvent('comment-changed', { detail: { value: text, index: i } });
    this.dispatchEvent(eventDis);
  }

  private _emitUserDeleted(i: number, id: string) {
    const eventDis = new CustomEvent('user-deleted', { detail: { value: id, index: i } });
    this.dispatchEvent(eventDis);
  }

  override render() {
    return html`
      <cr-flex horizontal space="sm" ${css({ flexWrap: 'wrap' })}>
        ${repeat(
          this.records.sort((a, b) => {
            const aVorname = (a.user?.vorname ?? this.emptyName).toUpperCase();
            const bVorname = (b.user?.vorname ?? this.emptyName).toUpperCase();
            return aVorname < bVorname ? -1 : aVorname > bVorname ? 1 : 0;
          }),
          r => r._id,
          (r, i) => {
            return html`<board-component-attendance-card
              .record=${r}
              @status-click=${(e: ValueEvent<Status>) => this._emitStatusClick(i, e.detail.value)}
              @comment-changed=${(e: ValueEvent<string>) => this._emitCommentChanged(i, e.detail.value)}
              @user-deleted=${(e: ValueEvent<string>) => this._emitUserDeleted(i, e.detail.value)}
              empty-name=${this.emptyName}
            ></board-component-attendance-card>`;
          },
        )}
      </cr-flex>
    `;
  }
}
